// src/beta/pages/Dossier.tsx
import { useParams } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Eyebrow } from '@/beta/components/Eyebrow';
import { RedRule } from '@/beta/components/RedRule';
import { SectionErrorBoundary } from '@/beta/components/SectionErrorBoundary';
import { DossierTitleBlock } from '@/beta/components/DossierTitleBlock';
import { CoverageScorecard } from '@/beta/components/CoverageScorecard';
import { ProvenanceTimeline } from '@/beta/components/ProvenanceTimeline';
import { DocumentsLibrary } from '@/beta/components/DocumentsLibrary';
import { MatchingTable } from '@/beta/components/MatchingTable';
import { AttributionList } from '@/beta/components/AttributionList';
import { StoryChapterList } from '@/beta/components/StoryChapterList';
import { PhasePhotoGrid } from '@/beta/components/PhasePhotoGrid';
import { getShowcaseBundle } from '@/beta/services/showcase';
import { getProvenance } from '@/beta/services/provenance';
import { listDocuments } from '@/beta/services/documents';
import { getVerification } from '@/beta/services/verification';
import { getMatchingComponents } from '@/beta/services/matchingComponents';
import { getAttributions } from '@/beta/services/attributions';
import { getStories } from '@/beta/services/stories';
import { getAnchor } from '@/beta/services/anchor';

const STALE = 5 * 60 * 1000;
const STALE_ANCHOR = 30 * 1000; // chain confirmations move

/** Inner component that throws on query error — caught by SectionErrorBoundary. */
function ProvenanceSection({ vehicleId, entries, isError, error }: {
  vehicleId: string;
  entries: { entries: { id: string; role: string; title: string; description: string | null; year_from: number | null; year_to: number | null; date_from: string | null; date_to: string | null; is_ongoing: boolean; location: string | null; verified: boolean }[]; total: number } | undefined;
  isError: boolean;
  error: Error | null;
}) {
  if (isError && error) throw error;
  return (
    <section>
      <Eyebrow>PROVENANCE — {entries?.total ?? 0} ENTRIES</Eyebrow>
      <RedRule width="full" />
      <div style={{ marginTop: 'var(--vm-s-3)' }}>
        <ProvenanceTimeline entries={entries?.entries ?? []} />
      </div>
    </section>
  );
}

/** § 6.4: Dossier page — proof layer composed of 8 sections. */
export function Dossier() {
  const { vehicleId = '' } = useParams<{ vehicleId: string }>();

  const queries = useQueries({
    queries: [
      { queryKey: ['beta', 'showcase', vehicleId, 'focus'], queryFn: () => getShowcaseBundle(vehicleId), staleTime: STALE },
      { queryKey: ['beta', 'provenance', vehicleId],        queryFn: () => getProvenance(vehicleId),        staleTime: STALE },
      { queryKey: ['beta', 'documents', vehicleId],         queryFn: () => listDocuments(vehicleId),        staleTime: STALE },
      { queryKey: ['beta', 'verification', vehicleId],      queryFn: () => getVerification(vehicleId),      staleTime: STALE },
      { queryKey: ['beta', 'matching', vehicleId],          queryFn: () => getMatchingComponents(vehicleId), staleTime: STALE },
      { queryKey: ['beta', 'attributions', vehicleId],      queryFn: () => getAttributions(vehicleId),      staleTime: STALE },
      { queryKey: ['beta', 'stories', vehicleId],           queryFn: () => getStories(vehicleId),           staleTime: STALE },
      { queryKey: ['beta', 'anchor', vehicleId],            queryFn: () => getAnchor(vehicleId),            staleTime: STALE_ANCHOR },
    ],
  });

  const [showcaseQ, provQ, docsQ, verQ, matchQ, attrQ, storyQ, anchorQ] = queries;

  if (showcaseQ.isLoading || verQ.isLoading) {
    return (
      <div data-testid="dossier-skeleton" style={{ padding: 'var(--vm-s-6)', color: 'var(--vm-stone)' }}>
        <Eyebrow>Loading dossier…</Eyebrow>
        <RedRule width="full" />
      </div>
    );
  }

  if (showcaseQ.isError || !showcaseQ.data) {
    return (
      <div style={{ padding: 'var(--vm-s-6)' }}>
        <Eyebrow>Dossier failed to load</Eyebrow>
        <RedRule width="full" />
        <p style={{ color: 'var(--vm-stone)', marginTop: 'var(--vm-s-2)' }}>
          The vehicle could not be resolved — try again from the catalog.
        </p>
      </div>
    );
  }

  const vehicleSummary = showcaseQ.data.vehicle;
  const vehicle = { id: vehicleSummary.id, name: vehicleSummary.display_name };
  const verification = verQ.data!;

  // Phase photos derived from showcase aggregator phases — Phase A returns
  // hero+count per phase, but for the dossier we want full image lists. If
  // /v1/vehicles/[id]/showcase ever exposes per-phase image arrays, swap to
  // those. For now we use the showcase phases and synthesize a single hero
  // image per phase as a placeholder — Phase H polish can extend the API.
  const phaseGroups = (showcaseQ.data.phases || []).map((p: any) => ({
    phase: p.phase,
    images: p.hero_image_url
      ? [{ id: `${p.phase}-hero`, url: p.hero_image_url, caption: null }]
      : [],
  }));

  return (
    <main
      style={{
        maxWidth: 1080,
        margin: '0 auto',
        padding: 'var(--vm-s-6) var(--vm-s-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--vm-s-8)',
      }}
    >
      {/* 1. Title block — not boundaried; if this fails the route boundary catches it */}
      <DossierTitleBlock
        vehicleName={vehicle.name}
        tier={verification.tier}
        coveragePercent={verification.coverage_percent}
        anchor={anchorQ.data ?? null}
      />

      {/* 2. Coverage scorecard */}
      <SectionErrorBoundary sectionName="Coverage">
        <section>
          <Eyebrow>COVERAGE — {verification.coverage_percent}%</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <CoverageScorecard verification={verification} />
          </div>
        </section>
      </SectionErrorBoundary>

      {/* 3. Provenance — uses inline throwing component so SEB catches query errors */}
      <SectionErrorBoundary sectionName="Provenance">
        <ProvenanceSection
          vehicleId={vehicleId}
          entries={provQ.data}
          isError={provQ.isError}
          error={provQ.error as Error | null}
        />
      </SectionErrorBoundary>

      {/* 4. Documents */}
      <SectionErrorBoundary sectionName="Documents">
        <section>
          <Eyebrow>DOCUMENTS — {docsQ.data?.total ?? 0}</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <DocumentsLibrary documents={docsQ.data?.documents ?? []} />
          </div>
        </section>
      </SectionErrorBoundary>

      {/* 5. Matching components */}
      <SectionErrorBoundary sectionName="Matching">
        <section>
          <Eyebrow>MATCHING — {matchQ.data?.components.length ?? 0}</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <MatchingTable components={matchQ.data?.components ?? []} />
          </div>
        </section>
      </SectionErrorBoundary>

      {/* 6. Restoration attributions */}
      <SectionErrorBoundary sectionName="Attributions">
        <section>
          <Eyebrow>ATTRIBUTIONS — {attrQ.data?.attributions.length ?? 0}</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <AttributionList attributions={attrQ.data?.attributions ?? []} />
          </div>
        </section>
      </SectionErrorBoundary>

      {/* 7. Story chapters */}
      <SectionErrorBoundary sectionName="Stories">
        <section>
          <Eyebrow>STORIES — {storyQ.data?.stories.length ?? 0}</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <StoryChapterList vehicleId={vehicleId} stories={storyQ.data?.stories ?? []} />
          </div>
        </section>
      </SectionErrorBoundary>

      {/* 8. Photo phases */}
      <SectionErrorBoundary sectionName="Photos">
        <section>
          <Eyebrow>PHOTOS — {phaseGroups.length} PHASES</Eyebrow>
          <RedRule width="full" />
          <div style={{ marginTop: 'var(--vm-s-3)' }}>
            <PhasePhotoGrid groups={phaseGroups} />
          </div>
        </section>
      </SectionErrorBoundary>
    </main>
  );
}
